import React from 'react';
import { motion } from 'framer-motion';
import { 
  Award, 
  Users, 
  Lightbulb, 
  Target, 
  Clock, 
  CheckCircle 
} from 'lucide-react';

const About = () => {
  const achievements = [
    {
      icon: Users,
      number: '50+',
      label: 'Local Businesses Transformed',
      description: 'Small to medium businesses across various industries'
    },
    {
      icon: Clock,
      number: '10+',
      label: 'Years Experience',
      description: 'Proven track record in digital marketing and AI automation'
    },
    {
      icon: Award,
      number: '98%',
      label: 'Client Satisfaction',
      description: 'Consistently exceeding expectations'
    }
  ];

  const values = [
    {
      icon: Target,
      title: 'Results-Driven',
      description: 'Every strategy is designed with measurable outcomes and ROI in mind.'
    },
    {
      icon: Lightbulb,
      title: 'Innovation First',
      description: 'We leverage cutting-edge AI and technology to stay ahead of the curve.'
    },
    {
      icon: CheckCircle,
      title: 'Transparency',
      description: 'Clear communication, honest reporting, and no hidden fees or surprises.'
    }
  ];

  return (
    <section id="about" className="section-padding gradient-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6"
              >
                <Award className="text-accent-400" size={16} />
                <span className="text-white text-sm font-medium">About Local Finder LLC</span>
              </motion.div>
              
              <h2 className="text-4xl lg:text-5xl font-black text-white mb-6">
                Transforming Local
                <span className="text-gradient block">Business Marketing</span>
              </h2>
              
              <p className="text-xl text-neutral-300 leading-relaxed mb-8">
                Located in Pasadena, CA, Local Finder LLC has been at the forefront of digital 
                marketing innovation for over 10 years. We specialize in empowering businesses 
                of all sizes - from local Pasadena companies to international brands - with 
                expert marketing strategies and cutting-edge AI automation.
              </p>
              
              <p className="text-lg text-neutral-400 leading-relaxed">
                Our mission is simple: GetBetterBiggerFasterVisibility for every client. 
                Whether you're seeking local dominance or global expansion, we combine proven 
                marketing methodologies with revolutionary AI technology to deliver results 
                that exceed expectations and drive sustainable growth worldwide.
              </p>
            </div>

            {/* Values */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-white mb-6">Our Core Values</h3>
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start space-x-4"
                >
                  <div className="w-12 h-12 bg-primary-500/20 backdrop-blur-sm border border-primary-500/30 rounded-xl flex items-center justify-center flex-shrink-0">
                    <value.icon className="text-primary-400" size={24} />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">{value.title}</h4>
                    <p className="text-neutral-400">{value.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Content - Image and Stats */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Team Image */}
            <motion.div
              className="relative rounded-2xl overflow-hidden shadow-2xl"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <img
                src="https://images.unsplash.com/photo-1606836591695-4d58a73eba1e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG1lZXRpbmd8ZW58MHx8fHwxNzQ4NzU2OTkxfDA&ixlib=rb-4.1.0&q=85"
                alt="Local Finder LLC Team"
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/60 to-transparent"></div>
              
              {/* Floating Contact Info */}
              <motion.div
                className="absolute bottom-6 left-6 glass rounded-xl p-4"
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <div className="text-white">
                  <div className="text-sm font-semibold">Pasadena, CA</div>
                  <div className="text-xs opacity-75">12 S Raymond Ave</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Achievement Stats */}
            <div className="grid grid-cols-3 gap-6">
              {achievements.map((achievement, index) => (
                <motion.div
                  key={achievement.label}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="glass rounded-2xl p-6 text-center"
                >
                  <div className="flex justify-center mb-3">
                    <achievement.icon className="text-primary-400" size={32} />
                  </div>
                  <div className="text-3xl font-bold text-white mb-2">{achievement.number}</div>
                  <div className="text-sm font-medium text-neutral-300 mb-1">{achievement.label}</div>
                  <div className="text-xs text-neutral-500">{achievement.description}</div>
                </motion.div>
              ))}
            </div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-6"
            >
              <h4 className="text-lg font-semibold text-white mb-4">Get in Touch</h4>
              <div className="space-y-3 text-neutral-300">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                  <span>contact@localfinder.biz</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-accent-400 rounded-full"></div>
                  <span>(213) 375-4006</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                  <span>12 S Raymond Ave, Pasadena, CA 91105</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;